import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, UsersThree, PencilSimple, TrashSimple, MagnifyingGlass, Star,
  CaretUp, CaretDown, ArrowsDownUp, HardHat, ArrowLeft, FilePdf,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { useSites } from "@/context/SiteContext";
import { useNavigate, useParams, Link } from "react-router-dom";

const emptyTeam = {
  name: "",
  leader_name: "", leader_phone: "",
  site_id: "",
  description: "",
};

/** 11 haneli '05551234567' → '0555 123 45 67'. Kısa girdilerde ham metin. */
const formatPhone = (s) => {
  const d = (s || "").replace(/\D/g, "");
  if (d.length === 11) return `${d.slice(0,4)} ${d.slice(4,7)} ${d.slice(7,9)} ${d.slice(9,11)}`;
  if (d.length === 10) return `0${d.slice(0,3)} ${d.slice(3,6)} ${d.slice(6,8)} ${d.slice(8,10)}`;
  return s || "";
};

export default function Teams() {
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const { sites } = useSites();
  const { siteId } = useParams();
  const site = useMemo(
    () => (sites || []).find((s) => s.id === siteId),
    [sites, siteId],
  );
  const isAdmin = !!user;
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyTeam, site_id: siteId || "" });
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "code", dir: "asc" });
  const [taseronMap, setTaseronMap] = useState({});  // team_id → subcontractor_id
  const navigate = useNavigate();

  const toggleSort = (key) => {
    setSort((s) => ({
      key,
      dir: s.key === key && s.dir === "asc" ? "desc" : "asc",
    }));
  };

  const load = async () => {
    try {
      const { data: t } = await axios.get(`${API}/teams`,
        { params: siteId ? { site_id: siteId } : {} });
      setTeams(t);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadTaseronMap = async () => {
    try {
      const { data } = await axios.get(`${API}/subcontractors`, {
        params: { kind: "taseron" },
      });
      const map = {};
      (data || []).forEach((s) => { if (s.team_id) map[s.team_id] = s.id; });
      setTaseronMap(map);
    } catch (e) { /* sessizce yut — bu izin başarısız olabilir */ }
  };
  useEffect(() => { load(); loadTaseronMap(); /* eslint-disable-next-line */ }, [siteId]);

  const registerAsTaseron = async (t) => {
    if (taseronMap[t.id]) {
      const ok = await askConfirm({
        title: "Zaten Kayıtlı",
        description: `"${t.name}" ekibi zaten taşeron olarak kayıtlı. Alt Yüklenici sayfasında görüntülemek ister misiniz?`,
        confirmText: "Görüntüle",
      });
      if (ok) navigate("/taseron/firmalar");
      return;
    }
    const ok = await askConfirm({
      title: "Taşeron Olarak Kaydet",
      description: `"${t.name}" ekibi Alt Yüklenici Yönetimi'ne "Taşeron" olarak eklenecek. Ekip adı, lider bilgileri ve telefonu otomatik doldurulacak.`,
      confirmText: "Kaydet",
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/subcontractors`, {
        kind: "taseron",
        team_id: t.id,
        name: t.name || "",
        contact_person: t.leader_name || "",
        contact_title: "Ekip Lideri",
        mobile: t.leader_phone || "",
        specialties: [],
        status: "aktif",
      });
      toast.success(`"${t.name}" taşeron olarak kaydedildi`);
      loadTaseronMap();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyTeam, site_id: siteId || "" });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || "",
      leader_name: t.leader_name || "",
      leader_phone: t.leader_phone || "",
      site_id: t.site_id || "",
      description: t.description || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Ekip adı zorunlu"); return; }
    if (!form.site_id) { toast.error("Çalıştığı şantiye zorunlu"); return; }
    const payload = {
      ...form,
      site_id: form.site_id || null,
      leader_employee_id: null,
    };
    try {
      if (editing) await axios.put(`${API}/teams/${editing.id}`, payload);
      else await axios.post(`${API}/teams`, payload);
      toast.success(editing ? "Ekip güncellendi" : "Ekip eklendi");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (t) => {
    const ok = await askConfirm({
      title: "Ekibi Sil",
      description: `"${t.name}" ekibini silmek istiyor musunuz? Bağlı personel varsa önce onları başka ekibe taşımalısınız.`,
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/teams/${t.id}`);
      toast.success("Silindi"); load();
    } catch (er) { toast.error(formatErr(er)); }
  };

  const filtered = useMemo(() => {
    const norm = (s) => (s || "").toLocaleLowerCase("tr-TR");
    const nq = norm(q);
    let list = teams;
    if (nq) {
      list = list.filter((t) =>
        norm(t.name).includes(nq) ||
        norm(t.leader_name).includes(nq),
      );
    }
    const getVal = (t) => {
      switch (sort.key) {
        case "code":    return (t.code || "zzz").toString();
        case "name":    return norm(t.name);
        case "site":    return norm(t.site_name);
        case "leader":  return norm(t.leader_name);
        case "members": return t.member_count || 0;
        default: return norm(t.name);
      }
    };
    const sorted = [...list].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va === vb) return 0;
      const cmp = va > vb ? 1 : -1;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [teams, q, sort]);

  const totalMembers = useMemo(
    () => teams.reduce((sum, t) => sum + (t.member_count || 0), 0),
    [teams],
  );

  return (
    <Shell
      title={site ? `${site.name} — Ekip Listesi` : "Ekip Listesi"}
      subtitle="Personel Yönetimi › Ekipler"
      hideSiteSelector
      actions={
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              const url = new URL(`${API}/teams/export.pdf`);
              if (siteId) url.searchParams.set("site_id", siteId);
              if (sort?.key) url.searchParams.set("sort_by", sort.key);
              if (sort?.dir) url.searchParams.set("sort_dir", sort.dir);
              window.open(url.toString(), "_blank", "noopener,noreferrer");
            }}
            data-testid="team-export-pdf"
            variant="outline"
            className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest border-[#1B4776] text-[#1B4776] hover:bg-[#1B4776] hover:text-white"
          >
            <FilePdf size={14} className="mr-1.5" /> PDF İndir
          </Button>
          {isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNew} data-testid="add-team"
                        className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white h-9 text-xs uppercase tracking-widest">
                  <Plus size={14} className="mr-1.5" /> Yeni Ekip
                </Button>
              </DialogTrigger>

              <DialogContent className="rounded-none sm:rounded-sm max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-bold font-heading">
                    {editing ? `Ekip Düzenle — ${editing.name}` : "Yeni Ekip"}
                  </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Ekip Adı</Label>
                    <Input value={form.name} data-testid="team-name"
                           onChange={(e) => set("name", e.target.value)}
                           placeholder="Duvar Ekibi, Boya Ekibi..."
                           className="mt-2 rounded-none sm:rounded-sm w-full" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    <div className="sm:col-span-3">
                      <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Ekip Lideri</Label>
                      <Input value={form.leader_name}
                             data-testid="team-leader"
                             onChange={(e) => set("leader_name", e.target.value)}
                             placeholder="Örn: Mehmet Polat"
                             className="mt-2 rounded-none sm:rounded-sm" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Lider Telefonu</Label>
                      <Input value={form.leader_phone}
                             data-testid="team-leader-phone"
                             onChange={(e) => set("leader_phone", e.target.value.replace(/\D/g, "").slice(0, 11))}
                             inputMode="numeric"
                             maxLength={11}
                             placeholder="05XXXXXXXXX"
                             className="mt-2 rounded-none sm:rounded-sm font-mono" />
                    </div>
                    <div className="sm:col-span-5 text-[10px] text-slate-500 -mt-1">
                      Lider ismi <b>Yazım Düzeni</b>ne çevrilir. Telefon 11 haneli olmalıdır (rakam dışı karakterler otomatik silinir).
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">
                      Çalıştığı Şantiye
                    </Label>
                    <div className="mt-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-none sm:rounded-sm text-sm"
                         data-testid="team-site-locked">
                      <span className="font-mono text-xs mr-2 text-slate-500">{site?.code || "—"}</span>
                      <span className="font-medium text-slate-900">{site?.name || "Şantiye seçilmemiş"}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      Ekip, seçili şantiyeye bağlı oluşturulur. Kodu proje bazlı otomatik verilir.
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Açıklama</Label>
                    <Textarea value={form.description}
                              onChange={(e) => set("description", e.target.value)}
                              rows={2} className="mt-2 rounded-none sm:rounded-sm w-full" />
                  </div>
                </div>

                <DialogFooter>
                  <Button onClick={save} data-testid="save-team"
                          className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                    {editing ? "Güncelle" : "Ekip Ekle"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Link to="/personel/ekipler">
            <Button variant="outline" data-testid="teams-back"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <ArrowLeft size={14} className="mr-1.5" /> Şantiye Listesi
            </Button>
          </Link>
        </div>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Toplam Ekip</div>
          <div className="text-2xl font-bold text-slate-900 font-heading">{teams.length}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Ekiplere Bağlı Personel</div>
          <div className="text-2xl font-bold text-slate-900 font-heading">{totalMembers}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Lidersiz Ekip</div>
          <div className="text-2xl font-bold text-amber-700 font-heading">
            {teams.filter((t) => !(t.leader_name || "").trim()).length}
          </div>
        </Card>
      </div>

      <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} data-testid="team-search"
                 onChange={(e) => setQ(e.target.value)}
                 placeholder="Ekip, lider ara..."
                 className="pl-9 rounded-none sm:rounded-sm" />
        </div>
      </Card>

      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <SortableTh label="Kod" sortKey="code" sort={sort} onSort={toggleSort} align="left" />
                <SortableTh label="Ekip Adı" sortKey="name" sort={sort} onSort={toggleSort} align="left" />
                <SortableTh label="Şantiye" sortKey="site" sort={sort} onSort={toggleSort} align="left" />
                <SortableTh label="Lider" sortKey="leader" sort={sort} onSort={toggleSort} align="left" />
                <SortableTh label="Üye" sortKey="members" sort={sort} onSort={toggleSort} align="center" />
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={t.id} className={i % 2 ? "bg-slate-50" : ""} data-testid={`team-row-${i}`}>
                  <td className="px-4 py-3 font-mono text-sm text-[#1B4776] font-bold">
                    {t.code || <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">{t.name}</td>
                  <td className="px-4 py-3 text-xs text-slate-700">
                    {t.site_code ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[10px] text-[#1B4776] font-bold">{t.site_code}</span>
                        <span className="text-slate-600 truncate max-w-[180px]">{t.site_name || ""}</span>
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {t.leader_name ? (
                      <div className="flex items-center gap-1.5">
                        <Star size={12} weight="fill" className="text-[#95C11F]" />
                        <div>
                          <div className="font-medium text-slate-800">{t.leader_name}</div>
                          {t.leader_phone && (
                            <a href={`tel:${t.leader_phone}`}
                               data-testid={`team-row-${i}-phone`}
                               className="text-[10px] font-mono text-[#556B10] hover:underline">
                              {formatPhone(t.leader_phone)}
                            </a>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-amber-700 text-[10px] uppercase tracking-widest font-bold">Atanmadı</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border bg-slate-100 text-slate-700 border-slate-300">
                      {t.member_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isAdmin && (
                      <>
                        <button onClick={() => registerAsTaseron(t)}
                                data-testid={`team-taseron-${i}`}
                                title={taseronMap[t.id]
                                  ? "Taşeron olarak zaten kayıtlı — Alt Yüklenici sayfasında görüntüle"
                                  : "Bu ekibi taşeron olarak Alt Yüklenici Yönetimi'ne kaydet"}
                                className={`w-7 h-7 inline-flex items-center justify-center hover:bg-[#F7FBE9] ${
                                  taseronMap[t.id] ? "opacity-100" : "opacity-90"
                                }`}>
                          <HardHat
                            size={14}
                            weight={taseronMap[t.id] ? "fill" : "bold"}
                            className={taseronMap[t.id] ? "text-[#556B10]" : "text-slate-500 hover:text-[#556B10]"}
                          />
                        </button>
                        <button onClick={() => openEdit(t)}
                                data-testid={`team-edit-${i}`}
                                className="w-7 h-7 inline-flex items-center justify-center hover:bg-slate-100">
                          <PencilSimple size={13} className="text-slate-600" />
                        </button>
                        <button onClick={() => del(t)}
                                data-testid={`team-delete-${i}`}
                                className="w-7 h-7 inline-flex items-center justify-center hover:bg-red-50">
                          <TrashSimple size={13} className="text-red-600" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <UsersThree size={32} weight="duotone" className="text-slate-400" />
                      <div>{q ? "Aramaya uygun ekip yok." : "Henüz ekip yok."}</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Shell>
  );
}

const SortableTh = ({ label, sortKey, sort, onSort, align = "left" }) => {
  const active = sort.key === sortKey;
  const dir = active ? sort.dir : null;
  const alignClass = align === "center" ? "text-center justify-center" : "text-left";
  return (
    <th className={`${alignClass} px-4 py-3 font-bold select-none`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`sort-${sortKey}`}
        className={`inline-flex items-center gap-1 group hover:text-[#1B4776] transition-colors ${
          active ? "text-[#1B4776]" : "text-slate-500"
        }`}
      >
        <span>{label}</span>
        {dir === "asc" && <CaretUp size={10} weight="fill" />}
        {dir === "desc" && <CaretDown size={10} weight="fill" />}
        {!active && <ArrowsDownUp size={10} className="opacity-40 group-hover:opacity-80" />}
      </button>
    </th>
  );
};

